import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Divider,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Warning,
} from '@mui/icons-material';
import {
  calculateRevenueSplit,
  generateExampleTransaction,
  formatCurrency,
  formatRateLabel,
} from '../utils/feeCalculator';

/**
 * FeePreview Component
 *
 * Displays a live-updating fee preview for ISO configuration.
 * Shows how revenue is split between ISO and Solvent based on current settings.
 *
 * Revenue Model (Share-of-Net):
 * - Solvent Buy Rate: Solvent's guaranteed minimum per transaction
 * - Markup = Total Fees - Solvent Buy Rate
 * - ISO gets: reseller_split_pct of markup
 * - Solvent gets: buy rate + remaining markup
 *
 * @param {Object} props
 * @param {number} props.solventBuyRatePct - Solvent's guaranteed percentage
 * @param {number} props.solventBuyRateUsd - Solvent's guaranteed flat USD
 * @param {number} props.resellerSplitPct - ISO's share of markup percentage
 * @param {number} [props.transactionAmount=100] - Example transaction amount
 * @param {boolean} [props.simplified=false] - Show simplified view for ISO admins
 * @param {number} [props.buyRatePct] - Legacy alias for solventBuyRatePct
 * @param {number} [props.buyRateUsd] - Legacy alias for solventBuyRateUsd
 */
export function FeePreview({
  solventBuyRatePct,
  solventBuyRateUsd,
  resellerSplitPct = 60,
  transactionAmount = 100,
  simplified = false,
  // Legacy prop names for backward compatibility
  buyRatePct,
  buyRateUsd,
  resellerSplitUsd, // Ignored in new model
}) {
  // Use new prop names, fallback to legacy
  const effectiveBuyRatePct = solventBuyRatePct ?? buyRatePct ?? 1;
  const effectiveBuyRateUsd = solventBuyRateUsd ?? buyRateUsd ?? 0.25;

  // Generate example transaction fees
  const exampleFees = generateExampleTransaction(transactionAmount);

  // Calculate revenue split with current settings
  const revenueSplit = calculateRevenueSplit({
    transactionAmount: exampleFees.transactionAmount,
    grossFees: exampleFees.grossFees,
    hardCosts: exampleFees.hardCosts,
    solventBuyRatePct: effectiveBuyRatePct,
    solventBuyRateUsd: effectiveBuyRateUsd,
    resellerSplitPct,
  });

  // Calculate bar widths for visualization
  const totalRevenue = revenueSplit.fiRevenue + revenueSplit.solventRevenue;
  const fiWidth = totalRevenue > 0 ? Math.max(0, (revenueSplit.fiRevenue / totalRevenue) * 100) : 50;
  const solventWidth = totalRevenue > 0 ? Math.max(0, (revenueSplit.solventRevenue / totalRevenue) * 100) : 50;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
          }}
        >
          <TrendingUp sx={{ fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#1e293b' }}>
          Revenue Preview
        </Typography>
        <Chip
          label={`$${transactionAmount} transaction`}
          size="small"
          sx={{
            ml: 'auto',
            backgroundColor: '#e0e7ff',
            color: '#4338ca',
            fontWeight: 500,
            fontSize: '12px',
          }}
        />
      </Box>

      {/* Warning for negative Solvent revenue */}
      {revenueSplit.isSolventNegative && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            mb: 2,
            backgroundColor: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: '8px',
          }}
        >
          <Warning sx={{ color: '#d97706', fontSize: 18 }} />
          <Typography sx={{ fontSize: '13px', color: '#92400e' }}>
            Solvent revenue is negative at this configuration. Consider adjusting rates.
          </Typography>
        </Box>
      )}

      {!simplified && (
        <>
          {/* Transaction breakdown */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Transaction Breakdown
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: '13px', color: '#475569' }}>Gross Fees</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#1e293b' }}>
                {formatCurrency(revenueSplit.grossFees)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: '13px', color: '#475569' }}>Hard Costs</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#dc2626' }}>
                -{formatCurrency(revenueSplit.hardCosts)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, borderTop: '1px dashed #e2e8f0' }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>Net Revenue</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                {formatCurrency(revenueSplit.netRevenue)}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Solvent Buy Rate calculation */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Solvent Buy Rate ({formatRateLabel(effectiveBuyRatePct, effectiveBuyRateUsd)})
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '13px', color: '#475569' }}>Solvent's Guaranteed Minimum</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#2563eb' }}>
                {formatCurrency(revenueSplit.solventBuyRateAmount)}
              </Typography>
            </Box>
          </Box>

          {/* Markup calculation */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Markup ({formatRateLabel(resellerSplitPct, 0, ' to ISO')})
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: '13px', color: '#475569' }}>Markup (Fees - Buy Rate)</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: revenueSplit.isNegativeMarkup ? '#dc2626' : '#1e293b' }}>
                {formatCurrency(revenueSplit.markup)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '13px', color: '#475569' }}>ISO Share of Markup</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#059669' }}>
                {formatCurrency(revenueSplit.isoShareOfMarkup)}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* Final revenue split */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Revenue Split
        </Typography>

        {/* Visual bar */}
        <Box sx={{ display: 'flex', height: 24, borderRadius: '6px', overflow: 'hidden', mb: 1.5 }}>
          <Box
            sx={{
              width: `${Math.max(fiWidth, 0)}%`,
              minWidth: revenueSplit.fiRevenue > 0 ? '20%' : '0%',
              backgroundColor: '#10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'width 0.3s ease',
            }}
          >
            {fiWidth > 20 && (
              <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#fff' }}>
                ISO
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: `${Math.max(solventWidth, 0)}%`,
              minWidth: revenueSplit.solventRevenue > 0 ? '20%' : '0%',
              backgroundColor: revenueSplit.solventRevenue >= 0 ? '#3b82f6' : '#dc2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'width 0.3s ease',
            }}
          >
            {solventWidth > 20 && (
              <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#fff' }}>
                Solvent
              </Typography>
            )}
          </Box>
        </Box>

        {/* Revenue amounts */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: '#10b981' }} />
            <Typography sx={{ fontSize: '13px', color: '#475569' }}>ISO Revenue</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#059669' }}>
              {formatCurrency(revenueSplit.fiRevenue)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '2px',
                backgroundColor: revenueSplit.solventRevenue >= 0 ? '#3b82f6' : '#dc2626',
              }}
            />
            <Typography sx={{ fontSize: '13px', color: '#475569' }}>Solvent</Typography>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: revenueSplit.solventRevenue >= 0 ? '#2563eb' : '#dc2626',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {revenueSplit.solventRevenue < 0 && <TrendingDown sx={{ fontSize: 14 }} />}
              {formatCurrency(revenueSplit.solventRevenue)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Percentages */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          pt: 1.5,
          borderTop: '1px solid #e2e8f0',
        }}
      >
        <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
          ISO: {revenueSplit.fiPctOfGross.toFixed(1)}% of gross
        </Typography>
        <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
          Solvent: {revenueSplit.solventPctOfGross.toFixed(1)}% of gross
        </Typography>
      </Box>
    </Paper>
  );
}

export default FeePreview;
